// StatCard — general-purpose stat tile mirroring FE bk `.stat-card`.
// Use for any "label + big value + optional sub" cell across the app
// (dashboard, reports, payroll summaries…). For widget cards in the
// dashboard the smaller-typography sibling `WidgetCard` keeps the
// children prop free-form.
import type { ReactNode } from "react";
import { BRAND_ART, BrandMark } from "./BrandArt";

type Props = {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  /** Optional icon shown right of the value. */
  icon?: ReactNode;
};

export function StatCard({ label, value, sub, icon }: Props) {
  return (
    <div className="group relative flex min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-surface p-3 shadow-sm transition-all hover:-translate-y-0.5 hover:border-[var(--color-border-light)] hover:shadow-md md:min-w-[180px] md:p-5">
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-[2px]"
        style={{ background: "var(--color-brand)" }}
      />
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-semibold uppercase tracking-[1px] text-muted-foreground">
            {label}
          </div>
          {/* Body font, not the brand serif: money must read plainly (CEO 25/10/2026). */}
          <div
            className="mt-2 text-[22px] font-normal leading-tight md:text-[28px]"
            style={{ color: "var(--color-text)" }}
          >
            {value}
          </div>
          {sub != null && (
            <div className="mt-1 text-xs text-muted-foreground">{sub}</div>
          )}
        </div>
        {icon != null ? (
          <div className="shrink-0 text-2xl opacity-70">{icon}</div>
        ) : (
          <BrandMark src={BRAND_ART.grain} className="mt-0.5 h-[18px] w-[18px] shrink-0 text-[var(--gn-gold)]/60" />
        )}
      </div>
    </div>
  );
}
